import axios from 'axios';
import { constant } from 'constant';

/**
 * Verify a workspace exists, and hydrate the three keys it owns.
 *
 * This is the ONLY writer of `tenantId`, `workspaceAgencyName` and `AgencyLogo`.
 * `services/api.js` sends `tenantId` as `x-tenant-id` on every request and
 * `services/socketService.js` refuses to connect without it — so any path that
 * reaches the sign-in form without calling this produces a form that looks
 * correct and then fails on submit. Logout clears all three, which is why the
 * auto-select resolver has to re-run this rather than just skipping the picker.
 *
 * Extracted from the workspace picker so the resolver can share it. It returns a
 * result rather than raising toasts because the two callers announce different
 * things: the picker narrates every outcome, the resolver only failure.
 */
export async function verifyWorkspace(name) {
	try {
		const res = await axios.get(
			`${constant.productBaseUrl}api/agencies/workspace/${name}`,
		);

		if (res?.status === 200) {
			const tenantId = res?.data?.data?.tenantId;

			// A 200 does not mean "found". The API in front of this answers an
			// unknown workspace with `{success:true, data:{}}` rather than the 404
			// its source suggests, so the status line cannot be trusted on its own.
			//
			// `tenantId` is the honest signal: it becomes the `x-tenant-id` header on
			// every request, so a workspace without one cannot produce a working
			// session no matter what else comes back. Checking it here is what stops
			// a deleted or renamed workspace from silently reaching the sign-in form.
			if (!tenantId) {
				return {
					ok: false,
					reason: 'not-found',
					message: 'Workspace not found.',
				};
			}

			const agencyName =
				res?.data?.data?.agencyName ||
				res?.data?.agencyName ||
				res?.data?.name ||
				name;
			const agencyLogo = res?.data?.data?.agencyLogo;

			// Written only past the tenantId gate, so a failed lookup cannot leave a
			// half-populated identity behind for the next screen to display.
			localStorage.setItem('workspaceAgencyName', agencyName);
			// Unconditional, which stores the string "undefined" when an agency has
			// no logo. Preserved from the original: it sits in the login path and
			// `useAgencyLogo` already copes. Worth fixing, but not as a side effect
			// of this change.
			localStorage.setItem('AgencyLogo', agencyLogo);
			localStorage.setItem('tenantId', tenantId);

			return { ok: true, agencyName, agencyLogo, tenantId };
		}

		return {
			ok: false,
			reason: 'not-found',
			message: res?.data?.message || 'Workspace not found.',
		};
	} catch (err) {
		if (err?.response?.status === 404) {
			return {
				ok: false,
				reason: 'not-found',
				message: err.response.data?.message,
			};
		}
		if (err?.response?.data?.message) {
			return {
				ok: false,
				reason: 'not-found',
				message: err.response.data.message,
			};
		}
		return {
			ok: false,
			reason: 'network',
			message: 'Failed to verify workspace. Please try again.',
		};
	}
}
